// `(fold ds [:a :b :c] :key-name :k :value-name :v)` — wide → long
// reshape. Top-level eager op; composes via threading.

import { z } from 'zod';

import { TypeMismatchError } from '../../errors.ts';
import { astShape, nodePos, opCallPos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { elemPath, paramPath } from '../../path.ts';
import { materializedToRows } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString, KeywordSchema } from '../../schema/keyword.ts';
import { type ColumnType, type Schema, schemaFromEntries, unknownCol } from '../../schema/types.ts';
import { type Cell, MaterializedDataset, type Value } from '../../value.ts';
import { columnNameAstName, requireColumns, resolveDsArg, rowsToColumns } from './helpers.ts';

/**
 * Decode a fold call's args at the AST layer: extract the melt-column
 * list (params[1]) and the resolved `:key-name` / `:value-name` kwargs
 * (with defaults). SSOT for "the shape `propagateSchema` and
 * `referencedColumns` need from a fold call" — apply uses Zod-parsed
 * values, but the engine hooks see the raw AST.
 */
type FoldShape = {
  readonly meltCols: readonly { readonly name: string; readonly path: string }[];
  readonly keyName: string;
  readonly valueName: string;
};

const decodeFoldShape = (data: unknown, basePath: string): FoldShape => {
  const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
  const meltShape = astShape(params[1]);
  const meltCols =
    meltShape.kind === 'vector'
      ? meltShape.elements.flatMap((el, i) => {
          const nm = columnNameAstName(el);
          return nm === undefined ? [] : [{ name: nm, path: elemPath(paramPath(basePath, 1), i) }];
        })
      : [];
  const kwargs = data as { readonly 'key-name'?: unknown; readonly 'value-name'?: unknown };
  const keyShape = astShape(kwargs['key-name']);
  const valueShape = astShape(kwargs['value-name']);
  const keyName = keyShape.kind === 'keyword' ? keyShape.name : 'key';
  const valueName = valueShape.kind === 'keyword' ? valueShape.name : 'value';
  return { meltCols, keyName, valueName };
};
/**
 * `(fold ds [:c1 :c2 :c3] :key-name :k :value-name :v)` — wide → long
 * reshape. The listed columns are melted into two output columns named
 * by `:key-name` (default `:key`) and `:value-name` (default `:value`).
 * Each input row produces N output rows (one per listed column).
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A listed name isn't a column on `ds.schema` →
 *     `UnknownColumnRefError` (with the dataset's columns as the
 *     "did you mean").
 *   - `:key-name` / `:value-name` collide with a preserved id column →
 *     `TypeMismatchError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * reshape runs.
 */
export const fold = z
  .object({
    name: z.literal('fold'),
    params: z.tuple([astArg('<dataset>'), z.array(ColumnNameSchema).min(1)]),
    'key-name': KeywordSchema.optional(),
    'value-name': KeywordSchema.optional(),
  })
  .describe(
    'Wide → long reshape. Vector of column names to melt; `:key-name` / `:value-name` name the output key/value columns.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(-> wells (fold [:q1 :q2 :q3] :key-name :quarter :value-name :revenue))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw, meltRaw] = data.params as readonly [Value, readonly ColumnName[]];
      const concrete = await resolveDsArg(dsRaw, '(fold ds [:a :b …] :key-name :k :value-name :v)', ctx);
      const meltCols = meltRaw.map(columnNameString);
      const keyName = data['key-name']?.name ?? 'key';
      const valueName = data['value-name']?.name ?? 'value';

      requireColumns(meltCols, concrete.schema, (idx) => nodePos(ctx.spans, paramPath(paramPath(ctx.path, 1), idx)));

      const idCols = Array.from(concrete.schema.keys()).filter((c) => !meltCols.includes(c));
      if (idCols.includes(keyName) || idCols.includes(valueName)) {
        throw new TypeMismatchError(
          'fresh key/value names',
          `:key-name \`${keyName}\` or :value-name \`${valueName}\` collides with an id column`,
          'fold',
          opCallPos(ctx.spans, ctx.path),
        );
      }

      const rows = materializedToRows(concrete);
      const outRows = rows.flatMap((row) =>
        meltCols.map((c) => ({
          ...Object.fromEntries(idCols.map((id) => [id, row[id] as Cell])),
          [keyName]: c as Cell,
          [valueName]: row[c] as Cell,
        })),
      );
      const outCols = rowsToColumns(outRows, [...idCols, keyName, valueName]);
      // 1:N — each input row melts into `meltCols.length` output rows that
      // all inherit the source row's instance identity (same iteration
      // order as `outRows`). Pass-through `undefined` when not instance-backed.
      const axis = concrete.identity;
      const identity = axis === undefined ? undefined : axis.flatMap((id) => meltCols.map(() => id));
      return new MaterializedDataset(
        outCols,
        propagateFoldSchema(concrete.schema, meltCols, keyName, valueName),
        identity,
      );
    },
    dataset: {
      // Output schema = id-cols (everything not in `meltCols`) + the
      // resolved key/value pair. Values come from heterogeneous columns
      // so the value column type is `unknown`.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
        const { meltCols, keyName, valueName } = decodeFoldShape(data, ctx.path);
        return propagateFoldSchema(
          ctx.inputSchema,
          meltCols.map((c) => c.name),
          keyName,
          valueName,
        );
      },
      // The listed melt cols are the only USAGE — key-name / value-name
      // are output definitions, excluded.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const { meltCols } = decodeFoldShape(data, basePath);
        return meltCols.map((c) => ({ name: c.name, path: c.path }));
      },
    },
  });

const propagateFoldSchema = (
  inputSchema: Schema,
  meltCols: readonly string[],
  keyName: string,
  valueName: string,
): Schema => {
  const idEntries: (readonly [string, ColumnType])[] = Array.from(inputSchema.entries()).filter(
    ([c]) => !meltCols.includes(c),
  );
  return schemaFromEntries([...idEntries, [keyName, unknownCol], [valueName, unknownCol]]);
};
